/**
 * CampusBar — PROD-1302. The campus name at the top of every Cafeteria Mode
 * surface, in the same position on all six routes, so a worker mid-service can
 * confirm which campus they are serving at a glance. The sidebar footer's
 * `text-xs text-muted-foreground` line was the thing reported as inadequate —
 * and below `md` it is not rendered at all (the cafeteria sidebar is
 * `hidden md:flex`, and cafeteria mode mounts no top bar), so on a tablet in
 * portrait this is the first campus text the mode has ever shown.
 *
 * Takes the id as a PROP rather than reading `useCafeteriaContextParams()`:
 * `ConfirmDeliveriesFlow` / `CompleteServiceFlow` are also embedded in the
 * production-record dialog (`components/production/complete-record-flow.tsx`),
 * scoped to the RECORD's campus (`doc.school.id`) while the 8h cafeteria lock
 * may still name a different one. A confidently wrong campus on a food-safety
 * screen is worse than no campus.
 *
 * Renders `locationName` (the campus — `Location.name`, filtered server-side to
 * `type: 'SCHOOL_CAMPUS'`) and NOT `schoolName`, which is the ORGANIZATION and
 * is therefore the same string on every campus of the multi-campus org this
 * ticket is about — it cannot disambiguate.
 */

import { Skeleton } from '@ordo/ui/skeleton'
import { School } from 'lucide-react'

import { pickCafeteriaLocation, useCafeteriaLocationsQuery } from '@/api/cafeteria'

export interface CampusBarProps {
  locationId: string
}

export function CampusBar({ locationId }: CampusBarProps) {
  const locationsQuery = useCafeteriaLocationsQuery()
  const campus = pickCafeteriaLocation(locationsQuery.data?.locations, locationId)
  // `fetchStatus === 'fetching'` is positive proof a request is actually in
  // flight. Offline, TanStack parks the query at pending/'paused' indefinitely
  // (networkMode defaults to 'online'); a skeleton there would pulse forever
  // and read as "loading" when the truth is "cannot confirm the campus".
  const resolving = locationsQuery.isPending && locationsQuery.fetchStatus === 'fetching'

  return (
    <div className="flex min-w-0 items-center gap-2">
      <School className="h-5 w-5 shrink-0 text-primary" aria-hidden />
      {campus ? (
        <p className="truncate text-lg font-semibold leading-tight">
          <span className="sr-only">Campus: </span>
          {campus.locationName}
        </p>
      ) : resolving ? (
        <Skeleton className="h-5 w-48" />
      ) : (
        <p className="truncate text-lg font-semibold leading-tight text-muted-foreground">Campus unavailable</p>
      )}
    </div>
  )
}
